import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { ArrowsClockwiseIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import {
  Badge,
  Button,
  Cell,
  Column,
  Modal,
  MultiSelect,
  Row,
  Select,
  Spinner,
  TableFrame,
  TableHeader,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';
import {
  buildExportPayload,
  EXPORT_VIEWS,
  toProviderOptions,
  type ExportView,
} from '../../lib/inventory-export';

type InventoryExport = {
  _id?: string;
  createdAt?: string;
  startTime?: string;
  endTime?: string;
  status?: string;
  url?: string;
  metadata?: { fileName?: string };
};

function SnapshotRange({ start, end }: { start?: string; end?: string }) {
  if (!start || !end) {
    return null;
  }

  const sameMonth = moment(start).isSame(end, 'month');

  return (
    <>
      {moment(start).format('MMM DD')}–
      {moment(end).format(sameMonth ? 'DD, YYYY' : 'MMM DD, YYYY')}
    </>
  );
}

function DownloadButton({ item }: { item: InventoryExport }) {
  const [loading, setLoading] = useState(false);

  const download = async () => {
    if (!item.url) return;
    setLoading(true);
    try {
      const response = await fetch(item.url);
      const blobUrl = window.URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = item.metadata?.fileName || 'inventory_export.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download file', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button size="sm" isDisabled={loading} onPress={download}>
      {loading ? (
        <>
          <Spinner size="sm" /> Loading...
        </>
      ) : (
        <>
          <DownloadSimpleIcon size={14} /> Download File
        </>
      )}
    </Button>
  );
}

type ExportInventoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Opened from one provider's page: the report starts scoped to it. */
  providerId?: string;
};

export function ExportInventoryModal({
  isOpen,
  onClose,
  providerId,
}: ExportInventoryModalProps) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [exports, setExports] = useState<InventoryExport[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [view, setView] = useState<ExportView>('consolidated');
  const [providerIds, setProviderIds] = useState<string[]>(
    providerId ? [providerId] : [],
  );
  const [providers, setProviders] = useState<unknown[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setProvidersLoading(true);
    StorageProviderService.getAllFPs()
      .then(response => {
        const list =
          response?.data?.providers ||
          response?.data ||
          response?.providers ||
          response;
        setProviders(Array.isArray(list) ? list : []);
      })
      .catch(error => {
        console.error('Failed to load providers', error);
        setProviders([]);
      })
      .finally(() => setProvidersLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    StorageProviderService.getInventoryExportHistory(page, limit)
      .then(response => {
        setExports(response?.data?.exports || []);
        setTotalCount(response?.totalCount || 0);
      })
      .catch(error => {
        console.error('Failed to fetch export history:', error);
      })
      .finally(() => setLoading(false));
  }, [isOpen, page, limit, refreshKey]);

  const providerOptions = useMemo(
    () => toProviderOptions(providers),
    [providers],
  );

  const generateReport = () => {
    StorageProviderService.createInventoryExport(
      buildExportPayload(view, providerIds),
    )
      .then(() => {
        ToastMessage.success('Report Generated');
        setRefreshKey(key => key + 1);
      })
      .catch(error => {
        ToastMessage.error(error.error);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={open => !open && onClose()}
      size="xl"
      title="Export Inventory"
      showCloseButton
      footer={<Button onPress={onClose}>Close</Button>}
    >
      <div className="sk:flex sk:flex-col sk:gap-4">
        <div className="sk:flex sk:flex-wrap sk:items-center sk:justify-end sk:gap-2">
          <Select
            aria-label="File type"
            options={EXPORT_VIEWS}
            value={view}
            onChange={value => setView(value as ExportView)}
            className="sk:w-44"
          />
          <MultiSelect
            aria-label="Fulfillment Provider"
            placeholder={
              providersLoading ? 'Loading providers…' : 'All providers'
            }
            searchPlaceholder="Search provider…"
            options={providerOptions}
            values={providerIds}
            onChange={setProviderIds}
            isDisabled={providersLoading}
            className="sk:w-56"
          />
          <Button variant="primary" onPress={generateReport}>
            Fetch Report
          </Button>
          <Button
            aria-label="Refresh"
            onPress={() => setRefreshKey(key => key + 1)}
          >
            <ArrowsClockwiseIcon size={16} />
          </Button>
        </div>

        <TableFrame
          aria-label="Inventory exports"
          columnCount={4}
          isLoading={loading}
          emptyMessage="No exports yet"
          pagination={{
            page,
            pageSize: limit,
            total: totalCount,
            onPageChange: setPage,
            onPageSizeChange: size => {
              setLimit(size);
              setPage(1);
            },
          }}
          header={
            <TableHeader>
              <Column id="exported" isRowHeader>
                Exported Date
              </Column>
              <Column id="snapshot">Snapshot Date</Column>
              <Column id="status">Order Status</Column>
              <Column id="file">File</Column>
            </TableHeader>
          }
        >
          {exports.map((item, index) => (
            <Row key={item._id ?? index} id={item._id ?? String(index)}>
              <Cell>
                {item.createdAt ? (
                  <span className="sk:flex sk:flex-col">
                    {moment(item.createdAt).format('MMM DD, YYYY')}
                    <span className="sk:text-fg-tertiary">
                      {moment(item.createdAt).format('hh:mm A')}
                    </span>
                  </span>
                ) : (
                  'N/A'
                )}
              </Cell>
              <Cell>
                <SnapshotRange start={item.startTime} end={item.endTime} />
              </Cell>
              <Cell>
                <Badge
                  tone={
                    item.status?.toLowerCase() === 'completed'
                      ? 'success'
                      : 'warning'
                  }
                >
                  {CommonUtility.toCapitalize(item.status) || 'N/A'}
                </Badge>
              </Cell>
              <Cell>
                <DownloadButton item={item} />
              </Cell>
            </Row>
          ))}
        </TableFrame>
      </div>
    </Modal>
  );
}
